import { computed, observable, action, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import chunk from 'lodash/chunk'
import {
  BehaviorSubject,
  merge,
  combineLatest,
  defer,
  firstValueFrom,
} from 'rxjs'
import { filter, tap, scan, map, switchMap } from 'rxjs/operators'
import { whileActive, shareLatest } from 'utils/rx-util'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { ViewModelList } from 'infra/ViewModelList'
import { NewUserTransactionViewModel } from './NewUserTransactionViewModel'
import {
  WorkspaceQueryPort,
  MemberQueryPort,
  AlertPort,
  UserQueryPort,
} from '../ports'
import { PendingTransactionViewModel } from './PendingTransactionViewModel'
import { CancelUserTransactionViewModel } from './CancelUserTransactionViewModel'
import PendingTransaction from '../PendingTransaction'
import {
  PayoutReviewStore,
  ListPendingTransactionsQuery,
} from '../PayoutReviewStore'
import { PayoutDraftingViewModel } from './PayoutDraftingViewModel'
import { fromStream, tapAction } from 'utils/mobx-util'
import { AlertState } from 'alerts/Alert'
import { csvDownload } from 'utils/csv-util'
import {
  DateRange,
  DateRangeFilter,
} from 'infra/FilterControls/DateRange/DateRangeFilter'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { FiltersViewModel, FilterItemSource } from 'infra/FiltersViewModel'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import { serializeUserTransactionReason } from '../../UserTransactionReason'
import {
  serializeTransactionFlag,
  TransactionFlag,
} from '../../TransactionFlag'
import { CursorPageQuery, CursorPaged, Id } from '../../../types/definitions'
import { TransactionSourceType } from '../../TransactionSourceType'
import { InfiniteSelectFilter } from 'infra/FilterControls/InfiniteSelect/InfiniteSelectFilter'

/**
 * The list selection state.
 */
export const enum SelectionState {
  /**
   * Nothing is selected at all.
   */
  NoneSelected = 'NoneSelected',
  /**
   * Some are selected.
   */
  SomeSelected = 'SomeSelected',
  /**
   * All are selected.
   */
  AllSelected = 'AllSelected',
}

/**
 * Filter for historical transactions.
 */
export type PendingTransactionFilter = {
  workspaces: Id[]
  createdRange: DateRange
  transactedRange: DateRange
  flags: TransactionFlag[]
  sources: TransactionSourceType[]
}

/**
 * View model for managing the overall payout review screen.
 */
export class PayoutReviewViewModel {
  /**
   * Tracks the VM's active status.
   *
   * @private
   */
  private readonly _active = new BehaviorSubject(false)

  /**
   * Pending transaction view models.
   */
  private readonly pendingTransactionVms

  /**
   * The selected transactions.
   */
  @observable private _selected = observable.set<PendingTransaction>()

  /**
   * The query to use.
   */
  private readonly query$ = defer(() =>
    combineLatest([
      this.searchText.debouncedValue$,
      this.filters.appliedFilters$,
    ])
  ).pipe(
    map(
      ([searchText, filters]): ListPendingTransactionsQuery => ({
        searchText,
        dateCreated: filters.createdRange,
        dateTransacted: filters.transactedRange,
        flags: filters.flags,
        sources: filters.sources,
        workspaceIds: filters.workspaces,
      })
    ),
    shareLatest()
  )

  /**
   * Fetch pipeline.
   */
  private readonly fetch$ = this.query$.pipe(
    whileActive(this._active),
    filter(() => this.canAccessPayoutsAdministration),
    switchMap((query) => {
      // Clear the selection when changing the filter.
      this.clearSelection()
      // Ignore errors here as we don't want to cancel the pipeline.
      return this.pendingTransactionsScroller.fetch(query).catch(() => null)
    })
  )

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Filters.
   */
  readonly filters = new FiltersViewModel<PendingTransactionFilter>({
    definitions: {
      createdRange: {
        icon: 'date-created',
        title: 'Date Created',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      transactedRange: {
        icon: 'date-transacted',
        title: 'Date Transacted',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      flags: {
        icon: 'flags',
        title: 'Flags',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static([
              {
                title: 'Job canceled',
                value: TransactionFlag.JobCanceled,
              },
              {
                title: 'Split auto-adjusted',
                value: TransactionFlag.JobPayoutAutoAdjusted,
              },
            ]),
            true
          ),
      },
      sources: {
        icon: 'transaction-source',
        title: 'Transaction source',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static([
              {
                title: 'User transactions',
                value: TransactionSourceType.User,
              },
              {
                title: 'Job transactions',
                value: TransactionSourceType.Job,
              },
            ]),
            true
          ),
      },
      workspaces: {
        icon: 'workspace-name',
        title: 'Workspaces',
        persist: true,
        factory: (key, title) => {
          return new InfiniteSelectFilter(
            key,
            title,
            (paging, _, filter) => this.listPlatformWorkspaces(paging, filter),
            true,
            true,
            3
          )
        },
      },
    },
  })

  /**
   * The cancellation view model.
   */
  @observable cancelUserTransactionVM: CancelUserTransactionViewModel | null =
    null

  /**
   * Scroller for the pending transactions.
   */
  readonly pendingTransactionsScroller = new InfiniteScrollProvider<
    PendingTransaction,
    ListPendingTransactionsQuery
  >({
    limit: 200,
    startFetchTaskAsPending: true,
    fetchItems: (paging, ct, query = {}) =>
      this.payoutReviewStore.listPendingTransactions(query, paging, ct),
  })

  /**
   * The view model for creating a new user transaction.
   */
  readonly newUserTransactionVM = new NewUserTransactionViewModel(
    this.payoutReviewStore,
    this.adapter,
    this._active
  )

  /**
   * The view model for drafting a payout (approve/remove transactions).
   */
  readonly draftingVM = new PayoutDraftingViewModel(
    this.payoutReviewStore,
    this.adapter
  )

  /**
   * Stream to Observable mapping.
   */
  private readonly $ = {
    /**
     * Track the manual transaction creation + cancellations in order
     * to keep a somewhat accurate count of the total pending transactions.
     */
    manualDelta: fromStream(
      0,
      merge(
        this.payoutReviewStore.pendingTransactionAdded$.pipe(map(() => 1)),
        this.payoutReviewStore.pendingTransactionRemoved$.pipe(map(() => -1))
      ).pipe(scan((accum, delta) => accum + delta))
    ),
  }

  /**
   * Constructor.
   */
  constructor(
    private payoutReviewStore: PayoutReviewStore,
    private adapter: WorkspaceQueryPort &
      MemberQueryPort &
      UserQueryPort &
      AlertPort
  ) {
    makeObservable(this)
    this.pendingTransactionVms = new ViewModelList({
      models: () =>
        // Filter out the ones that have already been approved
        this.pendingTransactionsScroller.items.filter(
          (pendingTransaction) =>
            !this.draftingVM.draft.approvedTransactionIds.has(
              pendingTransaction.id
            )
        ),
      create: (model) =>
        new PendingTransactionViewModel(
          model,
          (t) => this.selected.has(t),
          (t) => this.toggleSelection(t),
          () => this.showCancelTransactionModal(model),
          () => this.draftingVM.approveTransactions([model])
        ),
    })

    // When a new pending transaction is added, add it to the top of the
    // scroller list.
    this.payoutReviewStore.pendingTransactionAdded$
      .pipe(
        whileActive(this._active),
        filter((t) => !this.pendingTransactionsScroller.items.includes(t)),
        tapAction((t) => this.pendingTransactionsScroller.items.unshift(t))
      )
      .subscribe()

    // When a pending transaction is removed, remove it from the scroller list.
    this.payoutReviewStore.pendingTransactionRemoved$
      .pipe(
        whileActive(this._active),
        filter((t) => this.pendingTransactionsScroller.items.includes(t)),
        tapAction((t) => {
          this.pendingTransactionsScroller.items.splice(
            this.pendingTransactionsScroller.items.indexOf(t),
            1
          )
        })
      )
      .subscribe()

    // When payout completes, re-fetch the pending transactions
    this.draftingVM.payoutCompleted$
      .pipe(tap(() => this.pendingTransactionsScroller.fetch()))
      .subscribe()

    // Start the fetch pipeline.
    this.fetch$.subscribe()
  }

  /**
   * The pending visible transactions.
   */
  @computed
  get pendingTransactions() {
    return this.pendingTransactionVms.items
  }

  /**
   * Whether any filters have been applied, including search text.
   */
  @computed
  get filtersApplied() {
    return !!this.searchText.value || this.filters.appliedFilterCount > 0
  }

  /**
   * How many transactions are pending.
   */
  @computed
  get pendingTransactionsCount() {
    return this.pendingTransactionsScroller.total + this.$.manualDelta()
  }

  /**
   * Whether actions are disabled.
   */
  @computed
  get actionsDisabled() {
    return this.draftingVM.submit.pending
  }

  /**
   * Returns the selection, filtering out transactions that are already approved.
   */
  @computed
  get selected(): ReadonlySet<PendingTransaction> {
    const result = new Set<PendingTransaction>()
    for (const pendingTransaction of this._selected) {
      if (this.pendingTransactionVms.contains(pendingTransaction)) {
        result.add(pendingTransaction)
      }
    }

    return result
  }

  /**
   * The selection state.
   */
  @computed
  get selectionState(): SelectionState {
    if (this.pendingTransactions.length === 0) {
      return SelectionState.NoneSelected
    }

    const allSelected = this.pendingTransactions.every((tvm) =>
      this.selected.has(tvm.transaction)
    )

    if (allSelected) {
      return SelectionState.AllSelected
    }

    if (this.selected.size > 0) {
      return SelectionState.SomeSelected
    }

    return SelectionState.NoneSelected
  }

  /**
   * Whether the user can access the payouts administration.
   */
  @computed
  get canAccessPayoutsAdministration() {
    return this.adapter.getCurrentUser().platformAdmin
  }

  /**
   * Activates the view model.
   */
  @action.bound
  activate() {
    this._active.next(true)
    this.filters.activate()
    // Check that we are allowed to access the admin module.
    if (!this.canAccessPayoutsAdministration) {
      return
    }

    this.pendingTransactionVms.activate()
    this.draftingVM.activate()
  }

  /**
   * Deactivates the view model.
   */
  @action.bound
  deactivate() {
    this.filters.deactivate()
    this.pendingTransactionVms.deactivate()
    this.draftingVM.deactivate()
    this._active.next(false)
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Toggles the selection state for the given transaction.
   */
  @action.bound
  toggleSelection(transaction: PendingTransaction) {
    if (this.selected.has(transaction)) {
      this._selected.delete(transaction)
    } else {
      this._selected.add(transaction)
    }
  }

  /**
   * Toggles the selection based on the current list selection.
   */
  @action.bound
  toggleListSelection() {
    switch (this.selectionState) {
      case SelectionState.NoneSelected:
      case SelectionState.SomeSelected:
        this.pendingTransactions.forEach((tvm) =>
          this._selected.add(tvm.transaction)
        )
        break
      case SelectionState.AllSelected:
        this.clearSelection()
        break
    }
  }

  /**
   * Clears the selected transactions.
   */
  @action.bound
  clearSelection() {
    this._selected.clear()
  }

  /**
   * Shows the Cancel Transaction modal for the specified transaction.
   *
   * @param transaction
   */
  @action.bound
  showCancelTransactionModal(transaction: PendingTransaction) {
    this.cancelUserTransactionVM = new CancelUserTransactionViewModel(
      transaction,
      this.payoutReviewStore,
      this.adapter,
      this._active
    )
    this.cancelUserTransactionVM.show()
  }

  /**
   * Pages through all pending transactions and exports all of them as CSV.
   */
  exportCsv = task.resolved(async () => {
    const alert = this.adapter
      .newAlert()
      .withState(AlertState.Pending)
      .saying('Exporting', 'Retrieving data to export')
      .show()
    try {
      const items = await this.enumerateAllMatchingFilter()
      csvDownload(this.mapToCsv(items), 'pending-transactions.csv')
      alert
        .withState(AlertState.Success)
        .saying('Export completed!', 'Your download has started.')
        .autoDismiss()
    } catch (err) {
      alert.fromError(err).autoDismiss()
    }
  })

  /**
   * Approve the selected transactions.
   */
  approveSelectedTransactions = task.resolved(async () => {
    const selected = Array.from(this.selected)
    await this.draftingVM.approveTransactions(selected)
    this.clearSelection()
  })

  /**
   * Pages through all pending transactions and approves them all.
   */
  approveTransactionsMatchingFilter = task.resolved(async () => {
    const alert = this.adapter
      .newAlert()
      .withState(AlertState.Pending)
      .saying(
        'Approving',
        'Approving all transactions matching the current filter...'
      )
      .show()
    try {
      const items = await this.enumerateAllMatchingFilter()
      const chunks = chunk(items, 10_000)
      for (const batch of chunks) {
        await this.draftingVM.approveTransactions(batch)
      }
      alert.dismiss()
    } catch (err) {
      alert.fromError(err).autoDismiss()
    }
  })

  /**
   * Queries for all pending transactions matching the current filter.
   * @private
   */
  private async enumerateAllMatchingFilter() {
    const query = await firstValueFrom(this.query$)
    let cursor: string | null = null
    const items: PendingTransaction[] = []
    do {
      // Not sure why type annotation is needed ¯\_(ツ)_/¯
      // It complains about `page` initializer referencing itself.
      const page: CursorPaged<PendingTransaction> =
        await this.payoutReviewStore.listPendingTransactions(query, {
          limit: 1_000,
          after: cursor,
        })
      items.push(...page.data)
      cursor = page.cursor
    } while (cursor)
    return items
  }

  /**
   * Maps the transactions to CSV data.
   */
  private mapToCsv(transactions: PendingTransaction[]) {
    const header = [
      'Job ID',
      'Transaction Short ID',
      'Recipient User ID',
      'Amount',
      'Workspace ID',
      'Date Created',
      'Date Transacted',
      'Reason',
      'Flags',
    ]

    const csvData = transactions.map((tx) => {
      return [
        tx.jobId ?? '',
        tx.shortId,
        tx.recipient.publicId,
        tx.amount.toString(),
        tx.workspace.id,
        tx.createdAt.toLocaleDateString(),
        tx.transactedAt.toLocaleDateString(),
        tx.reason ? serializeUserTransactionReason(tx.reason) : '',
        tx.flags.map(serializeTransactionFlag).join(', '),
      ]
    })

    return [header, ...csvData]
  }

  /**
   * Lists the platform workspaces.
   */
  private listPlatformWorkspaces = async (
    page: CursorPageQuery,
    filter?: Record<string, unknown>
  ) =>
    this.adapter
      .listPlatformWorkspaces({
        ...filter,
        pagination: page,
      })
      .then((r) => ({
        ...r,
        data: r.data.map((w) => ({ value: w.id, title: w.name })),
      }))
}
